import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./modal.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInfo,
  faCheckCircle,
  faTimesCircle,
  faTriangleExclamation,
  faListUl,
  faLayerGroup,
  faLightbulb,
  faCircleQuestion,
  faChartLine,
  faClock,
} from "@fortawesome/free-solid-svg-icons";

const modalData = {
  title: "Guía de uso - Grupo de Ferreteria",
  subtitle:
    "La aplicación responde exclusivamente sobre costos, stock y movimientos de productos de Grupo de Ferreteria, utilizando cifras exactas del sistema de gestión.",
  sections: [
    {
      key: "how",
      icon: faListUl,
      heading: "Instrucciones de uso",
      body:
        "Se recomienda formular una sola consulta, clara y precisa. El asistente está entrenado para responder como un analista experto en ferretería:",
      bullets: [
        "Producto: indicar el nombre común (ejemplo: “martillo”, “cemento”, “pintura”), o bien su código o SKU.",
        "Desambiguación: si el nombre aplica a varios items, el sistema mostrará una lista para elegir el correcto.",
        "Periodo: especificar mes (ej: “enero”), año (ej: “2024”) o rango. Si se omite, se usa el periodo más reciente.",
        "Contexto: puedes preguntar sobre costos unitarios, stock en sucursales o variaciones de precio.",
        "Intención única: intenta hacer una pregunta a la vez para obtener la mejor respuesta.",
      ],
    },
    {
      key: "good",
      icon: faCheckCircle,
      heading: "Ejemplos de consultas adecuadas",
      goodExamples: [
        "¿Cuál es el costo unitario del cemento Portland en la sucursal Córdoba?",
        "Dime la variación de precio de la pintura látex blanca entre enero y febrero.",
        "¿Cuanto stock de tornillos autoperforantes queda en Palermo?",
        "Comparar el costo del martillo galponero vs martillo de bola.",
        "Listar los movimientos de stock del SKU 102030 en el último mes.",
      ],
    },
    {
      key: "bad",
      icon: faTimesCircle,
      heading: "Ejemplos de consultas no adecuadas",
      badExamples: [
        "“Dame todo sobre ventas y personal.” (muy amplio, enfócate en productos/costos)",
        "“¿Qué pasó con los precios desde 2010 hasta hoy?” (rango demasiado extenso)",
        "“¿Cómo se fabrica el cemento?” (pregunta técnica fuera de los datos de negocio)",
        "“¿Quién es el gerente de Córdoba?” (información de RRHH no disponible)",
        "“Hacer un gráfico de torta.” (no soportado visualmente aún).",
      ],
    },
    {
      key: "modes",
      icon: faLayerGroup,
      heading: "Modos de consulta",
      modes: [
        {
          tag: "Modo Búsqueda",
          desc:
            "Respuesta directa y resumida. Ideal para datos puntuales como precio actual o stock total.",
        },
        {
          tag: "Modo Detalle",
          desc:
            "Análisis profundo con desglose por SKU, sucursal o fechas específicas. Útil para reportes.",
        },
      ],
      tip: "Tip: Empieza con una pregunta general (Búsqueda) y si necesitas más datos, pide 'más detalles' para pasar a Modo Detalle.",
    },
    {
      key: "recs",
      icon: faLightbulb,
      heading: "Recomendaciones",
      bullets: [
        "Usa nombres específicos: 'Lija al agua grano 80' es mejor que solo 'lija'.",
        "Menciona la sucursal si te interesa un dato local (ej: 'en Mendoza').",
        "Si buscas variaciones, aclara los meses a comparar (ej: 'vs mes pasado').",
        "Revisa las sugerencias que aparecen bajo la respuesta para explorar temas relacionados.",
      ],
      scopeNote:
        "Alcance: Base de datos de Grupo de Ferreteria (Sucursales: Córdoba, Mendoza, Palermo, Rosario).",
      timeIcon: faClock,
    },
    {
      key: "errors",
      icon: faTriangleExclamation,
      heading: "Resolución de problemas",
      errors: [
        {
          title: "“No encontré productos con ese nombre”",
          fix: "Prueba con un sinónimo o el nombre más corto (ej: 'taladro' en vez de 'taladro percutor 600w').",
        },
        {
          title: "“Información no disponible”",
          fix: "Es posible que no haya datos para el periodo solicitado. Intenta con un mes anterior.",
        },
        {
          title: "“Consulta muy compleja”",
          fix: "Divide tu pregunta en dos partes más simples.",
        },
      ],
    },
    {
      key: "scope",
      icon: faChartLine,
      heading: "Alcance funcional",
      bullets: [
        "Datos de costos, precios, inventario y movimientos.",
        "4 sucursales: Córdoba, Mendoza, Palermo, Rosario.",
        "4 sucursales: Córdoba, Mendoza, Palermo, Rosario.",
        "Histórico de operaciones reciente (últimos 2-3 años).",
        "Moneda local (ARS) y referencias en USD cuando aplique.",
      ],
    },
  ],
};

export default function InfoModal() {
  const [isOpen, setIsOpen] = useState(false);
  const dialogRef = useRef(null);
  const openBtnRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    if (isOpen) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      dialogRef.current && dialogRef.current.focus();
    } else {
      openBtnRef.current && openBtnRef.current.focus();
    }
  }, [isOpen]);

  return (
    <>
      {/* Botón para abrir modal */}
      <button onClick={() => setIsOpen(true)} className="info" >
        <FontAwesomeIcon icon={faInfo} />
      </button>

      {/* Modal */}
      {isOpen && ReactDOM.createPortal(
        <div
          className="modal-overlay"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        >
          <div
            id="kc-costs-info-modal"
            className="modal-content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="kc-modal-title"
            aria-describedby="kc-modal-subtitle"
            onClick={(e) => e.stopPropagation()}
            tabIndex={-1}
            ref={dialogRef}
          >
            {/* Cerrar */}
            <button
              onClick={() => setIsOpen(false)}
              className="modal-close"
              aria-label="Cerrar modal"
              title="Cerrar"
            >
              ✕
            </button>

            <header className="modal-header">
              <div className="modal-title-wrap">
                <FontAwesomeIcon icon={faInfo} className="title-icon" />
                <h2 id="kc-modal-title">{modalData.title}</h2>
              </div>
              <p id="kc-modal-subtitle" className="modal-subtitle">
                {modalData.subtitle}
              </p>
            </header>

            <div className="modal-scroll">
              {modalData.sections.map((sec) => (
                <section className="modal-section" key={sec.key}>
                  <div className="section-heading">
                    <FontAwesomeIcon icon={sec.icon} className="section-icon" />
                    <h3>{sec.heading}</h3>
                  </div>

                  {/* Texto y bullets genéricos */}
                  {sec.body && <p className="section-body">{sec.body}</p>}
                  {sec.bullets && (
                    <ul className="bullets">
                      {sec.bullets.map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  )}

                  {/* Ejemplos buenos */}
                  {sec.goodExamples && (
                    <ul className="examples good">
                      {sec.goodExamples.map((ex, i) => (
                        <li key={i}>
                          <span className="chip chip-good">Adecuado</span>
                          <q>{ex}</q>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Ejemplos malos */}
                  {sec.badExamples && (
                    <ul className="examples bad">
                      {sec.badExamples.map((ex, i) => (
                        <li key={i}>
                          <span className="chip chip-bad">No adecuado</span>
                          <q>{ex}</q>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Modos */}
                  {sec.modes && (
                    <div className="modes-grid">
                      {sec.modes.map((m, i) => (
                        <div className="mode-card" key={i}>
                          <span className="chip chip-mode">{m.tag}</span>
                          <p>{m.desc}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {sec.tip && (
                    <div className="tip">
                      <FontAwesomeIcon icon={faCircleQuestion} />
                      <span>{sec.tip}</span>
                    </div>
                  )}

                  {/* Errores */}
                  {sec.errors && (
                    <div className="errors">
                      {sec.errors.map((e, i) => (
                        <div className="error-card" key={i}>
                          <div className="error-title">
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                            <strong>{e.title}</strong>
                          </div>
                          <p className="error-fix">
                            <span>Cómo resolver:</span> {e.fix}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Notas */}
                  {sec.scopeNote && (
                    <div className="scope-note">
                      <FontAwesomeIcon icon={faClock} />
                      <span>{sec.scopeNote}</span>
                    </div>
                  )}
                </section>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
